import { useState, useEffect, useCallback } from 'react';
import type { IFabricLakehouseTable, IFabricTableData } from '@skystone/shared';
import { fabricPreviewApi } from '@/services/fabric-preview-api';
import { cn, getErrorMessage } from '@/lib/utils';
import { useFabricEnvironment } from '@/hooks/use-fabric-environment';
import {
  PageHeader,
  PageLoading,
  ErrorBanner,
  DevEnvironmentBanner,
  DataTable,
  Spinner,
} from '@/components/ui';
import type { ColumnDef } from '@/components/ui';
import { inputClass } from '@/lib/form-styles';
import { PageContainer } from '@/components/layout';

// Constants

const DEFAULT_SCHEMA = 'gold';

// ── Module-level cell renderer (no handler closures) ─────────────────────────

function renderDynamicTableCell(value: unknown) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">NULL</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? 'true' : 'false'}</span>;
  }

  let str: string;
  if (typeof value === 'string') {
    str = value;
  } else if (typeof value === 'number') {
    str = String(value);
  } else {
    str = JSON.stringify(value);
  }

  return (
    <span className="max-w-xs truncate block" title={str}>
      {str}
    </span>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FabricTableBrowserPage() {
  const { isDev, lakehouseId, loading: envLoading } = useFabricEnvironment();

  // Table list state
  const [tables, setTables] = useState<IFabricLakehouseTable[]>([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [tablesError, setTablesError] = useState<string | null>(null);

  // Selected table + data state
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableData, setTableData] = useState<IFabricTableData | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // ---- Fetch tables in gold schema once lakehouseId is available ----
  const loadTables = useCallback(async (): Promise<void> => {
    if (!lakehouseId) return;
    setTablesLoading(true);
    setTablesError(null);
    try {
      const result = await fabricPreviewApi.getLakehouseTables(lakehouseId, DEFAULT_SCHEMA);
      setTables(result);
    } catch (err: unknown) {
      setTablesError(getErrorMessage(err, 'Failed to load tables'));
    } finally {
      setTablesLoading(false);
    }
  }, [lakehouseId]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // ---- Fetch table data when a table is selected ----
  const loadTableData = useCallback(async (tableName: string): Promise<void> => {
    if (!tableName || !lakehouseId) {
      setTableData(null);
      return;
    }
    setDataLoading(true);
    setDataError(null);
    setTableData(null);
    try {
      const result = await fabricPreviewApi.getLakehouseTablePreviewData(lakehouseId, DEFAULT_SCHEMA, tableName);
      setTableData(result);
    } catch (err: unknown) {
      setDataError(getErrorMessage(err, 'Failed to load table data'));
    } finally {
      setDataLoading(false);
    }
  }, [lakehouseId]);

  const handleTableChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const tableName = e.target.value;
    setSelectedTable(tableName);
    loadTableData(tableName);
  }, [loadTableData]);

  // ---- Build dynamic columns from table data ----
  const dynamicColumns: ColumnDef<Record<string, unknown>>[] = tableData
    ? tableData.columns.map((col) => ({
        id: col.name,
        header: col.name,
        accessor: col.name as keyof Record<string, unknown>,
        sortable: true,
        cell: renderDynamicTableCell,
      }))
    : [];

  // ---- Loading state ----
  if (envLoading || tablesLoading) {
    return <PageLoading />;
  }

  if (!lakehouseId) {
    return (
      <div className="space-y-6">
        <PageHeader title="Table Browser" subtitle="Preview top 100 rows from the metrics lakehouse (gold schema)" />
        <ErrorBanner message="No lakehouse configured. Set FABRIC_LAKEHOUSE_ID in the backend environment." />
      </div>
    );
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Table Browser"
        subtitle="Preview top 100 rows from the metrics lakehouse (gold schema)"
      />

      <DevEnvironmentBanner isDev={isDev} />

      <ErrorBanner message={tablesError} />

      {/* Table selector */}
      <div className="flex items-center gap-4">
        <label htmlFor="table-select" className="text-sm font-medium text-foreground">
          Select Table
        </label>
        <select
          id="table-select"
          className={cn(inputClass, 'max-w-sm')}
          value={selectedTable}
          onChange={handleTableChange}
          disabled={tables.length === 0}
        >
          <option value="">— Choose a table —</option>
          {tables.map((t) => (
            <option key={t.tableName} value={t.tableName}>
              {t.tableName}
            </option>
          ))}
        </select>
        {dataLoading && <Spinner size="sm" />}
      </div>

      <p className="text-sm text-muted-foreground">
        Preview only — this view does not run or alter production sync jobs.
      </p>

      <ErrorBanner message={dataError} />

      {/* Data table */}
      {tableData && (
        <div className="overflow-x-auto">
          <p className="text-sm text-muted-foreground mb-2">
            Showing {tableData.totalRowsReturned} row{tableData.totalRowsReturned === 1 ? '' : 's'} from{' '}
            <span className="font-mono text-foreground">{tableData.schemaName}.{tableData.tableName}</span>
          </p>
          <DataTable
            data={tableData.rows}
            columns={dynamicColumns}
            keyExtractor={(row) => String(tableData.rows.indexOf(row))}
            emptyMessage="This table has no data."
            enablePagination
            enableFilters={false}
            defaultPageSize={25}
          />
        </div>
      )}

      {/* Empty state when no table selected */}
      {!selectedTable && !dataLoading && (
        <div className="py-12 text-center">
          <p className="text-muted-foreground">Select a table above to preview (top 100 rows) without affecting sync.</p>
        </div>
      )}
    </PageContainer>
  );
}
